import React, { useState, useEffect, useCallback, useMemo } from 'react';

import {
    useAssignPrivateFundToDistributorsMutation,
    useGetDistributorCompaniesQuery,
} from 'api/main';
import { distributorSchemas } from 'schemas';
import { ORDER_BY } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionButton, ExpandableList, Loader, SuccessMessage, TransferList } from 'components';
import { Box } from '@mui/material';

const PrivateFunds = ({ fund }) => {
    const [assignPrivateFundToDistributors, { isLoading, isSuccess }] =
        useAssignPrivateFundToDistributorsMutation();
    const { data: { distributors = [] } = { distributors: [] }, isLoading: distributorsLoader } =
        useGetDistributorCompaniesQuery(
            distributorSchemas.getDistributorList({
                pagination: false,
                orderBy: ORDER_BY.distributorCompanyName,
            })
        );

    const filteredDistributors = useMemo(
        () => distributors.filter(d => d[0].user.emailConfirmed),
        [distributors]
    );

    const [selectedDistributors, setSelectedDistributors] = useState([]);
    const [errors, setErrors] = useState('');
    const [successfulUpdate, setSuccessfulUpdate] = useState(false);

    const handleChangeList = useCallback(value => {
        setSelectedDistributors(value);
        setSuccessfulUpdate(false);
        setErrors('');
    }, []);

    const handleSubmit = useCallback(() => {
        if (!selectedDistributors.length) {
            setErrors(texts.NEED_AT_LEAST_ONE_DISTIBUTOR);
        } else {
            assignPrivateFundToDistributors({
                id: fund.id,
                distributorsIds: selectedDistributors.map(sd => sd.id),
            });
        }
    }, [selectedDistributors, assignPrivateFundToDistributors, fund]);

    useEffect(() => {
        // set initial state
        if (filteredDistributors.length) {
            setSelectedDistributors(
                filteredDistributors
                    .map(d => d[0])
                    .filter(d => d.privateInvestmentFunds?.find?.(f => f.id === fund.id))
            );
        }
    }, [filteredDistributors, fund]);

    useEffect(() => {
        if (isSuccess) {
            setSuccessfulUpdate(true);
        }
    }, [isSuccess]);

    return (
        <ExpandableList
            title={texts.PRIVATE_FUNDS_FOR_DISTRIBUTORS}
            onCollapse={() => setSuccessfulUpdate(false)}
        >
            <Loader loading={distributorsLoader}>
                <TransferList
                    label={texts.CHOOSE_DISTIBUTORS_WITH_ACCESS}
                    renderText={d => d.corporateName}
                    items={filteredDistributors.map(d => d[0])}
                    value={selectedDistributors}
                    onChange={handleChangeList}
                    error={errors}
                />
                {successfulUpdate && <SuccessMessage message={texts.DATA_SAVED} />}
                <Box textAlign="center">
                    <ActionButton loading={isLoading} variant="contained" onClick={handleSubmit}>
                        {texts.SAVE}
                    </ActionButton>
                </Box>
            </Loader>
        </ExpandableList>
    );
};

export default PrivateFunds;
